import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import EdpiesLogo from '@/components/EdpiesLogo';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <div className="bg-slate-800 border-b border-slate-700 py-4 px-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)} 
            className="text-slate-300 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div onClick={() => navigate('/')} className="cursor-pointer">
            <EdpiesLogo size="small" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl md:text-4xl font-bold mb-8 text-center bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent">
          Terms of Service
        </h1>
        
        <div className="prose prose-invert prose-slate max-w-none space-y-8">
          <p className="text-slate-300 text-lg">
            Last updated: February 2025
          </p>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">1. Agreement to Terms</h2>
            <p className="text-slate-300">
              By accessing or using Edpies ("the Service"), you agree to be bound by these Terms of Service. If you disagree with any part of these terms, you may not access the Service. These terms apply to all visitors, users, and others who access or use the Service.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">2. Description of Service</h2>
            <p className="text-slate-300">
              Edpies is an educational technology platform that provides:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2">
              <li>Practice papers and mock exams for UK school curricula</li>
              <li>Diagnostic assessments and personalised learning plans</li>
              <li>Progress tracking and performance analytics</li>
              <li>Parent and tutor dashboards for monitoring student progress</li>
              <li>AI-powered learning assistance and challenges</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">3. User Accounts</h2>
            <p className="text-slate-300">
              When you create an account with us, you must provide accurate, complete, and current information. You are responsible for:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2">
              <li>Maintaining the confidentiality of your account and password</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorised use</li>
            </ul>
            <p className="text-slate-300 mt-4">
              For student accounts created by children under 13, a parent or guardian must provide consent and is responsible for the child's use of the Service.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">4. Acceptable Use</h2>
            <p className="text-slate-300">You agree not to:</p>
            <ul className="list-disc list-inside text-slate-300 space-y-2">
              <li>Use the Service for any unlawful purpose</li>
              <li>Share your account credentials with others</li>
              <li>Attempt to gain unauthorised access to any part of the Service</li>
              <li>Copy, modify, or distribute our content without permission</li>
              <li>Use automated systems to access the Service without our consent</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Submit false or misleading information</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">5. Intellectual Property</h2>
            <p className="text-slate-300">
              The Service and its original content, features, and functionality are owned by Edpies and are protected by international copyright, trademark, and other intellectual property laws. Our educational content, including questions, explanations, and learning materials, may not be reproduced, distributed, or used without our express written permission.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">6. Subscriptions and Payments</h2>
            <p className="text-slate-300">
              Some features of the Service require a paid subscription. By subscribing, you agree to:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2">
              <li>Pay all fees associated with your subscription plan</li>
              <li>Provide accurate billing information</li>
              <li>Automatic renewal unless cancelled before the renewal date</li>
            </ul>
            <p className="text-slate-300 mt-4">
              Refunds may be provided at our discretion and in accordance with applicable laws.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">7. Educational Content Disclaimer</h2>
            <p className="text-slate-300">
              While we strive to provide accurate and helpful educational content, Edpies:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2">
              <li>Does not guarantee specific educational outcomes or exam results</li>
              <li>Is not a substitute for formal education or professional tutoring</li>
              <li>May contain errors or inaccuracies in content</li>
              <li>Regularly updates content but cannot guarantee it reflects the latest curricula</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">8. Limitation of Liability</h2>
            <p className="text-slate-300">
              To the maximum extent permitted by law, Edpies shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or other intangible losses resulting from your use of the Service.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">9. Termination</h2>
            <p className="text-slate-300">
              We may terminate or suspend your account immediately, without prior notice, for any reason, including breach of these Terms. Upon termination, your right to use the Service will cease immediately. You may also delete your account at any time through your account settings.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">10. Changes to Terms</h2>
            <p className="text-slate-300">
              We reserve the right to modify or replace these Terms at any time. We will provide notice of any material changes by posting the new Terms on this page. Your continued use of the Service after any changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">11. Governing Law</h2>
            <p className="text-slate-300">
              These Terms shall be governed by and construed in accordance with the laws of England and Wales, without regard to its conflict of law provisions.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">12. Contact Us</h2>
            <p className="text-slate-300">
              If you have any questions about these Terms of Service, please contact us at:
            </p>
            <p className="text-slate-300">
              Email: legal@edpies.com
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-slate-800 py-6 px-4 text-center text-slate-500">
        <p>© 2025 Edpies. All rights reserved.</p>
      </div>
    </div>
  );
};

export default TermsOfService;
